import { clearToken, readToken } from '../features/auth/token-storage';
import { apiErrorFromBody, networkError, timeoutError } from './api-error';
import type { QueryParams, RequestOptions } from './types';

const DEFAULT_BASE_URL = '/api/v1';

/** Upper bound on any single request, so the UI can always settle. */
const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Relative by default so the same build works behind any host: the dev
 * server and the production nginx both proxy `/api` to the backend.
 */
const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL).replace(/\/+$/, '');

/**
 * 401 codes that describe a *submitted credential*, not a dead session.
 * A response carrying one of these must not clear the stored token — see the
 * 401 branch in `request` below.
 */
const CREDENTIAL_FAILURE_CODES = new Set(['INVALID_CREDENTIALS', 'ACCOUNT_INACTIVE']);

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Registered once by `AuthProvider`. Called after the token is cleared so the
 * app can drop its session state and send the user back to the login screen.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

function buildUrl(path: string, params?: QueryParams): string {
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }

  const queryString = search.toString();
  return queryString ? `${baseUrl}${normalisedPath}?${queryString}` : `${baseUrl}${normalisedPath}`;
}

function buildHeaders(hasBody: boolean, extra?: Record<string, string>): Headers {
  const headers = new Headers({ Accept: 'application/json' });
  if (hasBody) {
    headers.set('Content-Type', 'application/json');
  }
  const token = readToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  /**
   * Caller headers are merged **last** so a per-request header wins over the
   * default of the same name — the point of the option. `undefined` entries
   * are skipped rather than stringified into the literal `"undefined"`, which
   * an `Idempotency-Key` built from an unset value would otherwise send as a
   * valid-looking key and collide on for every caller.
   */
  for (const [name, value] of Object.entries(extra ?? {})) {
    if (value === undefined || value === null) continue;
    headers.set(name, value);
  }
  return headers;
}

async function parseJsonSafely(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const hasBody = options.body !== undefined;

  /**
   * Every request is bounded. A refused connection fails fast on its own, but
   * an upstream that accepts the socket and then never answers (a wedged
   * container, a dropped packet route) would otherwise leave the caller
   * pending forever — which the UI shows as a spinner that never resolves,
   * with no error and no way to retry. This guarantees the promise settles.
   */
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const abortFromCaller = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromCaller, { once: true });

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.params), {
      method,
      headers: buildHeaders(hasBody, options.headers),
      body: hasBody ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
  } catch (cause) {
    // The caller cancelled (query unmounted, superseded) — their intent, not
    // a failure to report. Our own timeout is a genuine connectivity failure.
    if (options.signal?.aborted) {
      throw cause;
    }
    if (controller.signal.aborted) {
      throw timeoutError();
    }
    throw networkError();
  } finally {
    clearTimeout(timeoutId);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }

  if (response.status === 401) {
    const error = apiErrorFromBody(401, await parseJsonSafely(response));

    /**
     * Not every 401 means "your session is gone".
     *
     * The credential endpoints answer 401 for a *submitted* credential that
     * was wrong — `POST /auth/login` and the current-password check on
     * `POST /auth/change-password`. Tearing the session down for those was a
     * real bug: typing your current password wrong on the change-password
     * screen signed you out instead of showing an inline error.
     *
     * The backend already distinguishes the two, so the rule is driven by the
     * error code rather than by a flag every call site has to remember to
     * pass: `UNAUTHENTICATED` (and anything unrecognised) is a dead session
     * and clears it; a credential rejection is the caller's to render.
     */
    if (!CREDENTIAL_FAILURE_CODES.has(error.code)) {
      clearToken();
      unauthorizedHandler?.();
    }

    throw error;
  }

  if (!response.ok) {
    const body = await parseJsonSafely(response);

    /**
     * The API always answers with the standard error envelope, so a 5xx with
     * no parseable body did not come from the API — it came from whatever sits
     * in front of it. The dev proxy and nginx both turn a refused upstream
     * connection into an empty 502/500, which is the "backend is down" case.
     * Report it as unreachable rather than as a generic server error.
     */
    if (body === undefined && response.status >= 500) {
      throw networkError();
    }

    throw apiErrorFromBody(response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await parseJsonSafely(response)) as T;
}

export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('GET', path, options),
  post: <T>(path: string, options?: RequestOptions) => request<T>('POST', path, options),
  put: <T>(path: string, options?: RequestOptions) => request<T>('PUT', path, options),
  patch: <T>(path: string, options?: RequestOptions) => request<T>('PATCH', path, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
};

export { baseUrl as apiBaseUrl };
